// i18n dictionary for the public payment-link page (PayLink.jsx). Covers the
// whole page: headings, form labels, buttons, reassurance and error messages.
// The link's admin-chosen `language` selects the locale; "auto" falls back to
// the buyer's browser language (mapped to a supported locale, else French).

export const PAYLINK_LANGS = [
  { code: 'auto', label: 'Auto (langue du visiteur)' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'it', label: 'Italiano' },
  { code: 'de', label: 'Deutsch' },
  { code: 'sv', label: 'Svenska' },
];

const SUPPORTED = ['fr', 'en', 'es', 'nl', 'it', 'de', 'sv'];

// Resolve the effective locale for a link. `configured` is the link's language
// ("auto" | fr | en | ...). On "auto" we read the browser language.
export const resolvePayLinkLang = (configured) => {
  if (configured && SUPPORTED.includes(configured)) return configured;
  try {
    const nav = (navigator.languages && navigator.languages[0]) || navigator.language || 'fr';
    const two = nav.slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(two)) return two;
  } catch { /* no navigator */ }
  return 'fr';
};

const T = {
  fr: {
    express_or: 'OU',
    summary_label: 'Récapitulatif',
    total_due: 'Total à payer',
    your_info_payment: 'Vos informations & paiement',
    subtotal: 'Sous-total',
    total: 'Total',
    taxes_incl: 'Taxes incluses',
    show_summary: 'Afficher le récapitulatif',
    hide_summary: 'Masquer le récapitulatif',
    step_contact: 'Coordonnées',
    step_shipping: 'Livraison',
    step_payment: 'Paiement',
    secure_all: 'Toutes les transactions sont sécurisées et chiffrées.',
    email: 'Email',
    first_name: 'Prénom',
    last_name: 'Nom',
    address: 'Adresse',
    city: 'Ville',
    postal_code: 'Code postal',
    country: 'Pays',
    phone_opt: 'Téléphone (option.)',
    phone: 'Téléphone',
    email_ph: 'vous@email.com',
    address_ph: 'Rechercher une adresse…',
    searching: 'Recherche…',
    loading_payment: 'Chargement du paiement sécurisé…',
    pay: 'Payer',
    processing: 'Traitement…',
    redirecting: 'Redirection…',
    card_gate_hint: 'Le paiement s’activera automatiquement une fois votre moyen de paiement renseigné.',
    card_gate_hint_short: 'Le paiement s’activera une fois votre moyen de paiement renseigné',
    secure_stripe: 'Paiement sécurisé par Stripe',
    secure_short: 'Paiement sécurisé',
    hosted_redirect_note: 'Vous serez redirigé vers la page sécurisée Stripe',
    unavailable_title: 'Lien indisponible',
    unavailable_desc: 'Ce lien de paiement n’existe plus.',
    brand_default: 'Paiement',
    err_loading: 'Chargement du paiement…',
    err_incomplete_contact: 'Merci de compléter vos coordonnées de livraison.',
    toast_incomplete_contact: 'Coordonnées incomplètes',
    err_card_missing: 'Merci de renseigner vos informations de carte avant de payer.',
    toast_card_missing: 'Informations de carte manquantes',
    err_check_payment: 'Vérifiez vos informations de paiement.',
    err_payment: 'Erreur de paiement',
    err_generic: 'Erreur',
    complete_info: 'Merci de compléter vos coordonnées.',
    session_unavailable: 'Session indisponible',
    payment_failed: 'Paiement impossible',
    generic_error: 'Une erreur est survenue',
  },
  en: {
    express_or: 'OR',
    summary_label: 'Summary',
    total_due: 'Total due',
    your_info_payment: 'Your details & payment',
    subtotal: 'Subtotal',
    total: 'Total',
    taxes_incl: 'Taxes included',
    show_summary: 'Show summary',
    hide_summary: 'Hide summary',
    step_contact: 'Contact',
    step_shipping: 'Shipping',
    step_payment: 'Payment',
    secure_all: 'All transactions are secure and encrypted.',
    email: 'Email',
    first_name: 'First name',
    last_name: 'Last name',
    address: 'Address',
    city: 'City',
    postal_code: 'Postal code',
    country: 'Country',
    phone_opt: 'Phone (optional)',
    phone: 'Phone',
    email_ph: 'you@email.com',
    address_ph: 'Search for an address…',
    searching: 'Searching…',
    loading_payment: 'Loading secure payment…',
    pay: 'Pay',
    processing: 'Processing…',
    redirecting: 'Redirecting…',
    card_gate_hint: 'Payment unlocks automatically once your payment method is filled in.',
    card_gate_hint_short: 'Payment unlocks once your payment method is filled in',
    secure_stripe: 'Secure payment by Stripe',
    secure_short: 'Secure payment',
    hosted_redirect_note: 'You will be redirected to Stripe’s secure page',
    unavailable_title: 'Link unavailable',
    unavailable_desc: 'This payment link no longer exists.',
    brand_default: 'Payment',
    err_loading: 'Loading payment…',
    err_incomplete_contact: 'Please complete your shipping details.',
    toast_incomplete_contact: 'Incomplete details',
    err_card_missing: 'Please enter your card details before paying.',
    toast_card_missing: 'Card details missing',
    err_check_payment: 'Please check your payment information.',
    err_payment: 'Payment error',
    err_generic: 'Error',
    complete_info: 'Please complete your details.',
    session_unavailable: 'Session unavailable',
    payment_failed: 'Payment failed',
    generic_error: 'Something went wrong',
  },
  es: {
    express_or: 'O',
    summary_label: 'Resumen',
    total_due: 'Total a pagar',
    your_info_payment: 'Tus datos y pago',
    subtotal: 'Subtotal',
    total: 'Total',
    taxes_incl: 'Impuestos incluidos',
    show_summary: 'Mostrar el resumen',
    hide_summary: 'Ocultar el resumen',
    step_contact: 'Datos de contacto',
    step_shipping: 'Envío',
    step_payment: 'Pago',
    secure_all: 'Todas las transacciones son seguras y cifradas.',
    email: 'Email',
    first_name: 'Nombre',
    last_name: 'Apellidos',
    address: 'Dirección',
    city: 'Ciudad',
    postal_code: 'Código postal',
    country: 'País',
    phone_opt: 'Teléfono (opcional)',
    phone: 'Teléfono',
    email_ph: 'tu@email.com',
    address_ph: 'Buscar una dirección…',
    searching: 'Buscando…',
    loading_payment: 'Cargando el pago seguro…',
    pay: 'Pagar',
    processing: 'Procesando…',
    redirecting: 'Redirigiendo…',
    card_gate_hint: 'El pago se activará automáticamente cuando completes tu método de pago.',
    card_gate_hint_short: 'El pago se activará al completar tu método de pago',
    secure_stripe: 'Pago seguro con Stripe',
    secure_short: 'Pago seguro',
    hosted_redirect_note: 'Serás redirigido a la página segura de Stripe',
    unavailable_title: 'Enlace no disponible',
    unavailable_desc: 'Este enlace de pago ya no existe.',
    brand_default: 'Pago',
    err_loading: 'Cargando el pago…',
    err_incomplete_contact: 'Completa tus datos de envío.',
    toast_incomplete_contact: 'Datos incompletos',
    err_card_missing: 'Introduce los datos de tu tarjeta antes de pagar.',
    toast_card_missing: 'Faltan los datos de la tarjeta',
    err_check_payment: 'Revisa tu información de pago.',
    err_payment: 'Error de pago',
    err_generic: 'Error',
    complete_info: 'Completa tus datos.',
    session_unavailable: 'Sesión no disponible',
    payment_failed: 'Pago imposible',
    generic_error: 'Ha ocurrido un error',
  },
  nl: {
    express_or: 'OF',
    summary_label: 'Overzicht',
    total_due: 'Te betalen',
    your_info_payment: 'Je gegevens & betaling',
    subtotal: 'Subtotaal',
    total: 'Totaal',
    taxes_incl: 'Inclusief btw',
    show_summary: 'Overzicht tonen',
    hide_summary: 'Overzicht verbergen',
    step_contact: 'Contactgegevens',
    step_shipping: 'Verzending',
    step_payment: 'Betaling',
    secure_all: 'Alle transacties zijn veilig en versleuteld.',
    email: 'E-mail',
    first_name: 'Voornaam',
    last_name: 'Achternaam',
    address: 'Adres',
    city: 'Stad',
    postal_code: 'Postcode',
    country: 'Land',
    phone_opt: 'Telefoon (optioneel)',
    phone: 'Telefoon',
    email_ph: 'jij@email.com',
    address_ph: 'Zoek een adres…',
    searching: 'Zoeken…',
    loading_payment: 'Beveiligde betaling laden…',
    pay: 'Betalen',
    processing: 'Verwerken…',
    redirecting: 'Doorverwijzen…',
    card_gate_hint: 'De betaling wordt automatisch geactiveerd zodra je je betaalmethode hebt ingevuld.',
    card_gate_hint_short: 'De betaling wordt geactiveerd zodra je je betaalmethode invult',
    secure_stripe: 'Veilige betaling via Stripe',
    secure_short: 'Veilige betaling',
    hosted_redirect_note: 'Je wordt doorgestuurd naar de beveiligde pagina van Stripe',
    unavailable_title: 'Link niet beschikbaar',
    unavailable_desc: 'Deze betaallink bestaat niet meer.',
    brand_default: 'Betaling',
    err_loading: 'Betaling laden…',
    err_incomplete_contact: 'Vul je verzendgegevens in.',
    toast_incomplete_contact: 'Gegevens onvolledig',
    err_card_missing: 'Vul je kaartgegevens in voordat je betaalt.',
    toast_card_missing: 'Kaartgegevens ontbreken',
    err_check_payment: 'Controleer je betaalgegevens.',
    err_payment: 'Betaalfout',
    err_generic: 'Fout',
    complete_info: 'Vul je gegevens in.',
    session_unavailable: 'Sessie niet beschikbaar',
    payment_failed: 'Betaling mislukt',
    generic_error: 'Er is een fout opgetreden',
  },
  it: {
    express_or: 'O',
    summary_label: 'Riepilogo',
    total_due: 'Totale da pagare',
    your_info_payment: 'I tuoi dati e pagamento',
    subtotal: 'Subtotale',
    total: 'Totale',
    taxes_incl: 'Tasse incluse',
    show_summary: 'Mostra il riepilogo',
    hide_summary: 'Nascondi il riepilogo',
    step_contact: 'Contatti',
    step_shipping: 'Spedizione',
    step_payment: 'Pagamento',
    secure_all: 'Tutte le transazioni sono sicure e crittografate.',
    email: 'Email',
    first_name: 'Nome',
    last_name: 'Cognome',
    address: 'Indirizzo',
    city: 'Città',
    postal_code: 'CAP',
    country: 'Paese',
    phone_opt: 'Telefono (opzionale)',
    phone: 'Telefono',
    email_ph: 'tu@email.com',
    address_ph: 'Cerca un indirizzo…',
    searching: 'Ricerca…',
    loading_payment: 'Caricamento del pagamento sicuro…',
    pay: 'Paga',
    processing: 'Elaborazione…',
    redirecting: 'Reindirizzamento…',
    card_gate_hint: 'Il pagamento si attiverà automaticamente una volta inseriti i dati di pagamento.',
    card_gate_hint_short: 'Il pagamento si attiverà una volta inseriti i dati di pagamento',
    secure_stripe: 'Pagamento sicuro con Stripe',
    secure_short: 'Pagamento sicuro',
    hosted_redirect_note: 'Sarai reindirizzato alla pagina sicura di Stripe',
    unavailable_title: 'Link non disponibile',
    unavailable_desc: 'Questo link di pagamento non esiste più.',
    brand_default: 'Pagamento',
    err_loading: 'Caricamento del pagamento…',
    err_incomplete_contact: 'Completa i tuoi dati di spedizione.',
    toast_incomplete_contact: 'Dati incompleti',
    err_card_missing: 'Inserisci i dati della carta prima di pagare.',
    toast_card_missing: 'Dati della carta mancanti',
    err_check_payment: 'Controlla le informazioni di pagamento.',
    err_payment: 'Errore di pagamento',
    err_generic: 'Errore',
    complete_info: 'Completa i tuoi dati.',
    session_unavailable: 'Sessione non disponibile',
    payment_failed: 'Pagamento non riuscito',
    generic_error: 'Si è verificato un errore',
  },
  de: {
    express_or: 'ODER',
    summary_label: 'Übersicht',
    total_due: 'Zu zahlen',
    your_info_payment: 'Deine Daten & Zahlung',
    subtotal: 'Zwischensumme',
    total: 'Gesamt',
    taxes_incl: 'Inkl. Steuern',
    show_summary: 'Übersicht anzeigen',
    hide_summary: 'Übersicht ausblenden',
    step_contact: 'Kontaktdaten',
    step_shipping: 'Versand',
    step_payment: 'Zahlung',
    secure_all: 'Alle Transaktionen sind sicher und verschlüsselt.',
    email: 'E-Mail',
    first_name: 'Vorname',
    last_name: 'Nachname',
    address: 'Adresse',
    city: 'Stadt',
    postal_code: 'Postleitzahl',
    country: 'Land',
    phone_opt: 'Telefon (optional)',
    phone: 'Telefon',
    email_ph: 'du@email.com',
    address_ph: 'Adresse suchen…',
    searching: 'Suche…',
    loading_payment: 'Sichere Zahlung wird geladen…',
    pay: 'Bezahlen',
    processing: 'Verarbeitung…',
    redirecting: 'Weiterleitung…',
    card_gate_hint: 'Die Zahlung wird automatisch aktiviert, sobald du deine Zahlungsmethode eingegeben hast.',
    card_gate_hint_short: 'Die Zahlung wird aktiviert, sobald du deine Zahlungsmethode eingibst',
    secure_stripe: 'Sichere Zahlung über Stripe',
    secure_short: 'Sichere Zahlung',
    hosted_redirect_note: 'Du wirst zur sicheren Stripe-Seite weitergeleitet',
    unavailable_title: 'Link nicht verfügbar',
    unavailable_desc: 'Dieser Zahlungslink existiert nicht mehr.',
    brand_default: 'Zahlung',
    err_loading: 'Zahlung wird geladen…',
    err_incomplete_contact: 'Bitte vervollständige deine Versanddaten.',
    toast_incomplete_contact: 'Unvollständige Daten',
    err_card_missing: 'Bitte gib deine Kartendaten ein, bevor du bezahlst.',
    toast_card_missing: 'Kartendaten fehlen',
    err_check_payment: 'Bitte überprüfe deine Zahlungsdaten.',
    err_payment: 'Zahlungsfehler',
    err_generic: 'Fehler',
    complete_info: 'Bitte vervollständige deine Daten.',
    session_unavailable: 'Sitzung nicht verfügbar',
    payment_failed: 'Zahlung fehlgeschlagen',
    generic_error: 'Ein Fehler ist aufgetreten',
  },
  sv: {
    express_or: 'ELLER',
    summary_label: 'Sammanfattning',
    total_due: 'Att betala',
    your_info_payment: 'Dina uppgifter & betalning',
    subtotal: 'Delsumma',
    total: 'Totalt',
    taxes_incl: 'Moms ingår',
    show_summary: 'Visa sammanfattning',
    hide_summary: 'Dölj sammanfattning',
    step_contact: 'Kontaktuppgifter',
    step_shipping: 'Frakt',
    step_payment: 'Betalning',
    secure_all: 'Alla transaktioner är säkra och krypterade.',
    email: 'E-post',
    first_name: 'Förnamn',
    last_name: 'Efternamn',
    address: 'Adress',
    city: 'Stad',
    postal_code: 'Postnummer',
    country: 'Land',
    phone_opt: 'Telefon (valfritt)',
    phone: 'Telefon',
    email_ph: 'du@email.com',
    address_ph: 'Sök efter en adress…',
    searching: 'Söker…',
    loading_payment: 'Laddar säker betalning…',
    pay: 'Betala',
    processing: 'Behandlar…',
    redirecting: 'Omdirigerar…',
    card_gate_hint: 'Betalningen aktiveras automatiskt när du har fyllt i din betalningsmetod.',
    card_gate_hint_short: 'Betalningen aktiveras när du fyllt i din betalningsmetod',
    secure_stripe: 'Säker betalning via Stripe',
    secure_short: 'Säker betalning',
    hosted_redirect_note: 'Du omdirigeras till Stripes säkra sida',
    unavailable_title: 'Länk otillgänglig',
    unavailable_desc: 'Den här betalningslänken finns inte längre.',
    brand_default: 'Betalning',
    err_loading: 'Laddar betalning…',
    err_incomplete_contact: 'Fyll i dina fraktuppgifter.',
    toast_incomplete_contact: 'Ofullständiga uppgifter',
    err_card_missing: 'Ange dina kortuppgifter innan du betalar.',
    toast_card_missing: 'Kortuppgifter saknas',
    err_check_payment: 'Kontrollera dina betalningsuppgifter.',
    err_payment: 'Betalningsfel',
    err_generic: 'Fel',
    complete_info: 'Fyll i dina uppgifter.',
    session_unavailable: 'Session otillgänglig',
    payment_failed: 'Betalning misslyckades',
    generic_error: 'Ett fel uppstod',
  },
};

export const payLinkT = (lang) => T[lang] || T.fr;
export default T;
